<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { onClickOutside, onKeyStroke } from "@vueuse/core";

export interface SankeyNode {
  code: string;
  label: string;
  total: number;
  flow: number;
  color: string;
}

interface ChartLink {
  source: string;
  target: string;
  nb: number;
}

const props = defineProps({
  sources: { type: Array as () => SankeyNode[], required: true },
  targets: { type: Array as () => SankeyNode[], required: true },
  links: { type: Array as () => ChartLink[], required: true },
  respondents: { type: Number, default: 0 },
});

// The SVG scales on its viewBox, so the diagram always fits the card.
const WIDTH = 900;
const HEIGHT = 440;
const NODE_WIDTH = 14;
const NODE_GAP = 10;
const PADDING_Y = 8;
// 26 characters of 12px text fit the label column with the count on its own line below.
const LABEL_WIDTH = 190;
const LABEL_MAX_CHARS = 26;
const LEFT_X = LABEL_WIDTH;
const RIGHT_X = WIDTH - LABEL_WIDTH - NODE_WIDTH;

type Side = "source" | "target";

interface PlacedNode extends SankeyNode {
  side: Side;
  y: number;
  height: number;
  // Running offset stacking the ribbons along the node.
  offset: number;
}

interface Ribbon {
  path: string;
  color: string;
  nb: number;
  source: PlacedNode;
  target: PlacedNode;
}

function placeColumn(nodes: SankeyNode[], side: Side): Record<string, PlacedNode> {
  const total = nodes.reduce((sum, node) => sum + node.total, 0);
  const available = HEIGHT - 2 * PADDING_Y - NODE_GAP * Math.max(0, nodes.length - 1);
  let y = PADDING_Y;
  const placed: Record<string, PlacedNode> = {};
  nodes.forEach((node) => {
    const height = total > 0 ? Math.max(3, (node.total / total) * available) : 3;
    placed[node.code] = { ...node, side, y, height, offset: 0 };
    y += height + NODE_GAP;
  });
  return placed;
}

const layout = computed(() => {
  const left = placeColumn(props.sources, "source");
  const right = placeColumn(props.targets, "target");

  // Ribbons stack in node order so they never cross inside a node.
  const ribbons: Ribbon[] = [];
  for (const source of props.sources) {
    for (const target of props.targets) {
      const link = props.links.find((l) => l.source === source.code && l.target === target.code);
      if (!link || link.nb <= 0) continue;

      const from = left[source.code];
      const to = right[target.code];
      const fromHeight = (link.nb / Math.max(from.flow, 1)) * from.height;
      const toHeight = (link.nb / Math.max(to.flow, 1)) * to.height;

      const x0 = LEFT_X + NODE_WIDTH;
      const x1 = RIGHT_X;
      const y0 = from.y + from.offset;
      const y1 = to.y + to.offset;
      from.offset += fromHeight;
      to.offset += toHeight;

      const cx = (x0 + x1) / 2;
      const path = [
        `M ${x0} ${y0}`,
        `C ${cx} ${y0}, ${cx} ${y1}, ${x1} ${y1}`,
        `L ${x1} ${y1 + toHeight}`,
        `C ${cx} ${y1 + toHeight}, ${cx} ${y0 + fromHeight}, ${x0} ${y0 + fromHeight}`,
        "Z",
      ].join(" ");

      ribbons.push({
        path,
        color: from.color,
        nb: link.nb,
        source: from,
        target: to,
      });
    }
  }

  return { left: Object.values(left), right: Object.values(right), ribbons };
});

type Target = { kind: "ribbon"; idx: number } | { kind: "node"; side: Side; code: string };

// Hovering only lights the flows; a click pins the details panel where the user
// clicked, and a second click on the same element, the background or Escape closes it.
const hovered = ref<Target | null>(null);
const selected = ref<Target | null>(null);
const active = computed(() => selected.value ?? hovered.value);

const wrapper = ref<HTMLElement | null>(null);
const cursor = ref({ x: 0, y: 0, flip: false });

function sameTarget(a: Target | null, b: Target) {
  if (!a || a.kind !== b.kind) return false;
  if (a.kind === "ribbon" && b.kind === "ribbon") return a.idx === b.idx;
  if (a.kind === "node" && b.kind === "node") return a.side === b.side && a.code === b.code;
  return false;
}

function select(target: Target, event: MouseEvent) {
  if (sameTarget(selected.value, target)) {
    selected.value = null;
    return;
  }
  const rect = wrapper.value?.getBoundingClientRect();
  if (rect) {
    const x = event.clientX - rect.left;
    cursor.value = { x, y: event.clientY - rect.top, flip: x > rect.width / 2 };
  }
  selected.value = target;
}

// New data, a click anywhere outside the chart or Escape close the pinned panel.
watch(() => props.links, () => (selected.value = null));
onClickOutside(wrapper, () => (selected.value = null));
onKeyStroke("Escape", () => (selected.value = null));

const panelStyle = computed(() => ({
  left: `${cursor.value.x}px`,
  top: `${cursor.value.y}px`,
  transform: cursor.value.flip ? "translate(calc(-100% - 14px), 14px)" : "translate(14px, 14px)",
}));

function nodeHovered(node: PlacedNode) {
  return active.value?.kind === "node" && active.value.side === node.side && active.value.code === node.code;
}

function ribbonLit(ribbon: Ribbon, idx: number) {
  if (!active.value) return null;
  if (active.value.kind === "ribbon") return active.value.idx === idx;
  return nodeHovered(ribbon.source) || nodeHovered(ribbon.target);
}

// The lit ribbons stand out, the rest fades; nothing hovered keeps the resting opacity.
function ribbonOpacity(ribbon: Ribbon, idx: number) {
  const lit = ribbonLit(ribbon, idx);
  if (lit === null) return 0.3;
  return lit ? 0.75 : 0.08;
}

function nodeOpacity(node: PlacedNode) {
  if (!active.value) return 1;
  if (active.value.kind === "node") return nodeHovered(node) ? 1 : 0.35;
  const ribbon = layout.value.ribbons[active.value.idx];
  return ribbon && (ribbon.source === node || ribbon.target === node) ? 1 : 0.35;
}

const formatCount = (value: number) => Math.round(value).toLocaleString();
const formatShare = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : "–");

const hoverPanel = computed(() => {
  if (!selected.value) return null;
  const { ribbons } = layout.value;

  if (selected.value.kind === "ribbon") {
    const ribbon = ribbons[selected.value.idx];
    if (!ribbon) return null;
    return {
      title: `${ribbon.source.label} → ${ribbon.target.label}`,
      color: ribbon.color,
      count: ribbon.nb,
      rows: [
        { label: "of the base", value: formatShare(ribbon.nb, props.respondents) },
        { label: `of ${ribbon.source.label}`, value: formatShare(ribbon.nb, ribbon.source.total) },
        { label: `of ${ribbon.target.label}`, value: formatShare(ribbon.nb, ribbon.target.total) },
      ],
    };
  }

  const { side, code } = selected.value;
  const column = side === "source" ? layout.value.left : layout.value.right;
  const node = column.find((n) => n.code === code);
  if (!node) return null;
  // A source node lists where its respondents go, a target node where they come from.
  const flows = ribbons
    .filter((ribbon) => (side === "source" ? ribbon.source : ribbon.target) === node)
    .map((ribbon) => ({
      label: side === "source" ? ribbon.target.label : ribbon.source.label,
      color: side === "source" ? ribbon.target.color : ribbon.source.color,
      value: `${formatCount(ribbon.nb)} · ${formatShare(ribbon.nb, node.total)}`,
    }));
  return {
    title: node.label,
    color: node.color,
    count: node.total,
    rows: [{ label: "of the base", value: formatShare(node.total, props.respondents) }],
    flows,
  };
});

function shortLabel(label: string) {
  return label.length > LABEL_MAX_CHARS ? `${label.slice(0, LABEL_MAX_CHARS - 1)}…` : label;
}

const labelX = (node: PlacedNode) => (node.side === "source" ? LEFT_X - 8 : RIGHT_X + NODE_WIDTH + 8);
</script>

<template>
  <div ref="wrapper" class="relative w-full">
    <svg
      :viewBox="`0 0 ${WIDTH} ${HEIGHT}`"
      class="h-auto w-full"
      preserveAspectRatio="xMidYMid meet"
      @click="selected = null">
      <path
        v-for="(ribbon, idx) in layout.ribbons"
        :key="`ribbon-${idx}`"
        :d="ribbon.path"
        :fill="ribbon.color"
        :fill-opacity="ribbonOpacity(ribbon, idx)"
        class="cursor-pointer transition-[fill-opacity] duration-150"
        @mouseenter="hovered = { kind: 'ribbon', idx }"
        @mouseleave="hovered = null"
        @click.stop="select({ kind: 'ribbon', idx }, $event)" />

      <g
        v-for="node in [...layout.left, ...layout.right]"
        :key="`${node.side}-${node.code}`"
        :opacity="nodeOpacity(node)"
        class="cursor-pointer transition-opacity duration-150"
        @mouseenter="hovered = { kind: 'node', side: node.side, code: node.code }"
        @mouseleave="hovered = null"
        @click.stop="select({ kind: 'node', side: node.side, code: node.code }, $event)">
        <rect
          :x="node.side === 'source' ? LEFT_X : RIGHT_X"
          :y="node.y"
          :width="NODE_WIDTH"
          :height="node.height"
          rx="2"
          :fill="node.color" />
        <text :text-anchor="node.side === 'source' ? 'end' : 'start'" class="fill-foreground text-xs">
          <tspan :x="labelX(node)" :y="node.y + node.height / 2 - 2">{{ shortLabel(node.label) }}</tspan>
          <tspan :x="labelX(node)" :y="node.y + node.height / 2 + 2" dominant-baseline="hanging" class="fill-muted-foreground">
            {{ formatCount(node.total) }}
          </tspan>
        </text>
      </g>
    </svg>

    <div
      v-if="hoverPanel"
      class="pointer-events-none absolute z-10 w-max max-w-[280px] rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground shadow-md"
      :style="panelStyle">
      <div class="flex flex-col gap-1">
        <span class="flex items-center gap-1.5 font-semibold">
          <span class="inline-block h-2 w-2 shrink-0 rounded-full" :style="{ backgroundColor: hoverPanel.color }" />
          <span class="truncate">{{ hoverPanel.title }}</span>
        </span>
        <span class="tabular-nums"><strong>{{ formatCount(hoverPanel.count) }}</strong> respondents</span>
        <div class="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-0.5 tabular-nums">
          <template v-for="(row, rowIdx) in hoverPanel.rows" :key="rowIdx">
            <span class="text-right font-medium">{{ row.value }}</span>
            <span class="truncate opacity-90">{{ row.label }}</span>
          </template>
        </div>
        <div
          v-if="hoverPanel.flows?.length"
          class="mt-0.5 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 border-t border-primary-foreground/20 pt-1 tabular-nums">
          <template v-for="(flow, flowIdx) in hoverPanel.flows" :key="flowIdx">
            <span class="flex items-center gap-1.5 truncate">
              <span class="inline-block h-1.5 w-1.5 shrink-0 rounded-full" :style="{ backgroundColor: flow.color }" />
              <span class="truncate">{{ flow.label }}</span>
            </span>
            <span class="text-right opacity-90">{{ flow.value }}</span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
